<template>
    <Drawer initialWidth="3xl" :closeOnClickOutside="true" @requestUnmount="hide">

		<template #header>
			<div class="font-semibold text-md w-full">
				{{ $tc('Observation',2) }}
				<div v-if="pupil" class="flex justify-between items-center w-3/5 mx-auto">
					<button type="button" class="btn btn-circle btn-sm" @click="toNeighbouringPupil('previous')">
						<ChevronLeftIcon class="w-4 h-4" />
					</button>
					<Transition>
					<div v-if="currentPupil" class="flex gap-2 w-full items-center justify-center">
						<!-- <PupilSymbol :pupil="currentPupil" class="w-12" /> -->
						<span class="font-bold text-lg">{{ currentPupil.getFullName() }}</span>
					</div>
					</Transition>
					<button type="button" class="btn btn-circle btn-sm" @click="toNeighbouringPupil('next')">
						<ChevronRightIcon class="w-4 h-4" />
					</button>
				</div>
			</div>
		</template>

        <template #default="{ hide: drawerHide }">

            <div class="pb-6 px-2">
				<div class="sticky top-0 z-30 bg-base px-2 pt-6">
					<div class="flex flex-col gap-6 border-b pb-2">
						<!-- Within a classroom the drawer is scoped to that classroom, unlike the school-wide view. Make widening it an explicit choice.
							Same switch as the "show notes" toggle in PupilGoalEvaluation. -->
						<div v-if="classroom" class="print:hidden flex items-center gap-2 text-sm">
							<SwitchRoot
								:modelValue="includeOtherClassrooms"
								id="includeOtherClassrooms"
								@update:modelValue="setIncludeOtherClassrooms($event)"
								class="w-[32px] h-[20px] shadow-sm flex data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-gray-800 border border-gray-300 data-[state=checked]:border-stone-700 rounded-full relative transition-[background]"
							>
								<SwitchThumb class="w-3.5 h-3.5 my-auto bg-white text-xs flex items-center justify-center shadow-xl rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-full" />
							</SwitchRoot>
							<label for="includeOtherClassrooms" class="hover:cursor-pointer">{{ $t('Also show observations made in other classrooms') }}</label>
						</div>

						<ObservationsFilter class="print:hidden" storeKey="observationsDrawer" :showLastSchoolYearPreset="true" :withPupilSelect="false" />
					</div>
				</div>

                <!-- <FloatingPlusButton :pulsate="!observationsStore.loading && observationsStore.items.length === 0 ? true : false" @push="handleObservationCreateRequest" /> -->
				<EmptyState v-if="loaded && !observationsStore.loading && observationsStore.items.length === 0"
					:title="$t('No observations found for this period')"
				/>

				<div v-else-if="observationsStore.loading" class="flex justify-center py-12">
					<span class="loading loading-spinner loading-sm"></span>
				</div>

               	<ObservationsList v-else
					:observations="observationsStore.items"
					:sortOrder="observationsStore.order"
					@sortOrderChanged="observationsStore.applyOrder"
					:showPupilColumn="pupil === null"
					:max-thumbnails="1"
					:readOnly="readOnly"
					:classroomId="classroom?.id ?? null"
				/>

				<!-- Load more -->
				<div>
					<div v-if="observationsStore.loadingMore" class="flex justify-center mt-2 mb-2">
						<span class="loading loading-spinner loading-sm"></span>
					</div>
					<div v-if="!(observationsStore.loading || observationsStore.loadingMore) && observationsStore.page?.hasNext()" class="text-center">
						<button type="button" @click="observationsStore.loadMore" class="btn btn-sm mt-4">
							{{ $t('Load more') }}
						</button>
					</div>
				</div>

				<div class="drawer-action-bar justify-end gap-2">
					<button type="button" class="btn btn-ghost" @click="drawerHide">
						{{ $t('Close') }}
					</button>
				</div>
            </div>

        </template>
    </Drawer>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { PropType } from "vue";

// Store Imports
import { usePupilsStore } from "@/stores/Pupils.store";
import { useForkedObservationsStore } from "@/stores/Observations.store";
import { usePupilActivitiesStore } from "@/stores/PupilActivities.store";
import { FilterParamValue } from "@/stores/filters/FilterParamValue";

// UI Components
import Drawer from '@/components/ui/Drawer.v2.vue';
import ObservationsList from "@/components/ObservationsList.vue";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import EmptyState from "@/components/ui/EmptyState.vue";

// Feature Components
import ObservationsFilter from "./ObservationsFilter.vue";

// Icons & Models
import { PlusIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import { SwitchRoot, SwitchThumb } from 'reka-ui';
import { Pupil } from "@/models/Pupil.model";
import { Classroom } from "@/models/Classroom.model";


export default {

    emits: ['requestUnmount'],

    props: {
        pupil: {
            type: Object as PropType<Pupil | null>,
            default: null
        },
		readOnly: {
			type: Boolean,
			default: false
		},
		classroom: {
			type: Object as PropType<Classroom | null>,
			default: null
		}
    },

    components: {
        Drawer,
        ObservationsFilter,
		ObservationsList,
		PupilSymbol,
        PlusIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon,
		EmptyState,
		SwitchRoot, SwitchThumb,
    },

    data() {
        return {
			loaded: false,
            // editableModel: null as Observation | null,
            currentPupil: null as Pupil | null,
			initialPupil: null as Pupil | null,
        };
    },

    computed: {
        ...mapStores(
			usePupilsStore,
			usePupilActivitiesStore,
			useForkedObservationsStore('observationsDrawer')
		),

		// proxy/shortcut function
        observationsStore() {
			// return useForkedObservationsStore('observationsDrawer')();	// alternative for the mapStores call
			return this.observationsDrawerStore;
		},

		/** The classroom scope this drawer opens with: its classroom, or none at school level. */
		defaultClassroomIds(): string[] {
			return this.classroom?.id ? [this.classroom.id] : [];
		},

		/** Derived from the filter itself, so a filter reset unticks the checkbox too. */
		includeOtherClassrooms(): boolean {
			return this.observationsStore.getCurrentFilter().classroomIds.length === 0;
		},
    },

	async mounted() {

		if(this.pupil?.id) {
			this.currentPupil = this.pupil;
			this.initialPupil = this.pupil;

			await Promise.all([
				this.loadObservationsForCurrentPupil(this.defaultClassroomIds),
				this.pupilActivitiesStore.loadActivityOnDate(new Date()),
				this.pupilsStore.loadOnce()	// loading the other pupils to navigate between them in the drawer
			]);

		}
		this.loaded = true;
	},

    methods: {

		/**
		 * (Re)load the observations of the current pupil.
		 * @param classroomIds When given, replaces the classroom scope of the filter. When omitted
		 *                     (e.g. while browsing to another pupil) the current scope is kept.
		 */
		async loadObservationsForCurrentPupil(classroomIds: string[] | null = null) {

			if(!this.currentPupil) return;

			const pupilFilterParam = new FilterParamValue(
					this.currentPupil.id,
					this.currentPupil.getFullName()
				);

			// The default filter is what "Reset" returns to: this pupil, in this drawer's classroom scope.
			const newDefaultFilter = this.observationsStore.getDefaultFilter();
			newDefaultFilter.setParamValue('pupilIds', pupilFilterParam);
			newDefaultFilter.classroomIds = this.defaultClassroomIds;
			this.observationsStore.setDefaultFilter(newDefaultFilter);

			const currentFilter = this.observationsStore.getCurrentFilter();
			currentFilter.setParamValue('pupilIds', pupilFilterParam);
			if (classroomIds !== null) {
				currentFilter.classroomIds = classroomIds;
			}

			await this.observationsStore.applyFilter(currentFilter);

		},

		setIncludeOtherClassrooms(include: boolean) {
			this.loadObservationsForCurrentPupil(include ? [] : this.defaultClassroomIds);
		},

		toNeighbouringPupil(direction: 'previous' | 'next') {
			if(!this.currentPupil) return;
			const currentIndex = this.pupilsStore.items.findIndex(p => p.id === this.currentPupil?.id);
			if(currentIndex === -1) return;
			const newIndex = direction === 'previous' ? currentIndex - 1 : currentIndex + 1;
			if(newIndex < 0 || newIndex >= this.pupilsStore.items.length) return;
			this.currentPupil = this.pupilsStore.items[newIndex];
			this.loadObservationsForCurrentPupil();
		},

        hide() {
            this.$emit('requestUnmount');
        },

        // async handleObservationCreateRequest() {
        //     const pupil = this.pupilsStore.findById(this.selectedPupilId);
        //     if (!pupil) return;
        //     this.editableModel = this.observationsStore.new(pupil);
        //     this.showObservationEditor = true;
        // },

    }
};
</script>
